/**
 * Types for Timeseries API
 */

// Common types for both PerformanceHistory and Rolling Stats
export interface TimeRange {
  start_date?: string;
  end_date?: string;
  preset?: string;
}

export interface Resource {
  resource_type: "product" | "portfolio" | "benchmark" | "index";
  resource_id: string;
  stream_type:
    | "total_return"
    | "TOTAL_RETURN"
    | "price"
    | "PRICE"
    | "gross_return"
    | "GROSS_RETURN"
    | "net_return"
    | "NET_RETURN"
    | "default";
}

export type Frequency = "daily" | "weekly" | "monthly" | "quarterly" | "quarterly_anchored" | "yearly" | "yearly_anchored";

// PerformanceHistory specific types
export interface TimeseriesRequest {
  time_range: TimeRange;
  frequency: Frequency;
  resources: Resource[];
  display_mode?: "index" | "performance";
  base_value?: number;
  extend_incomplete_with_proxy?: boolean;
}

export interface TimeseriesDataPoint {
  date: string;
  [resourceId: string]: string | number;
}

export interface TimeseriesResponse {
  data: TimeseriesDataPoint[];
  metadata: {
    resources: {
      [resourceId: string]: {
        name: string;
        type: string;
        stream_type: string;
      };
    };
    time_range: TimeRange;
    frequency: Frequency;
  };
}

// Rolling Stats specific types
export type StatisticType =
  | "volatility"
  | "returns"
  | "sharpe_ratio"
  | "sortino_ratio"
  | "max_drawdown"
  | "beta"
  | "correlation"
  | "underwater";

export interface RollingStatsRequest {
  resources: Resource[];
  time_range: TimeRange;
  frequency: Frequency;
  statistic: StatisticType;
  window_size: number;
  window_frequency: Frequency;
  extend_incomplete_with_proxy?: boolean;
  // Note: For beta/correlation, each resource is compared against its OWN benchmark
  // which is looked up from the resource's snapshot on the backend.
}

export interface ProxyExtensionMeta {
  real_data_starts?: string | null;
  real_data_ends?: string | null;
  extended_at_start?: boolean;
  extended_at_end?: boolean;
  proxy_benchmark_id?: string;
  proxy_benchmark_name?: string;
}

export interface RollingStatsDataPoint {
  date: string;
  [resourceId: string]: string | number;
}

export interface MetricTimeseriesRequest {
  resources: Resource[];
  field_path: string;
  time_range: TimeRange;
  frequency: Frequency;
}

export interface MetricTimeseriesResponse {
  data: {
    cols: { id: string; label: string; type: string }[];
    rows: (string | number | null)[][];
    statistics?: Record<string, unknown>;
  };
  metadata: {
    field_path: string;
    frequency: Frequency;
    time_range: TimeRange;
    resources: Record<
      string,
      {
        name: string;
        type: string;
        resource_id: string;
      }
    >;
  };
}

export interface RollingStatsResponse {
  data: RollingStatsDataPoint[];
  metadata: {
    resources: {
      [resourceId: string]: {
        name: string;
        type: string;
        stream_type: string;
      };
    };
    time_range: TimeRange;
    frequency: Frequency;
    statistic: StatisticType;
    window_size: number;
    window_frequency: Frequency;
  };
}

// Correlation Matrix specific types
export interface CorrelationMatrixRequest {
  resources: Resource[];
  time_range: TimeRange;
  frequency: Frequency;
}

export interface CorrelationMatrixCell {
  resource1: string;
  resource2: string;
  correlation: number;
}

export interface CorrelationMatrixResponse {
  matrix: number[][]; // 2D correlation matrix
  resources: string[]; // Resource IDs in order
  resource_names: Record<string, string>; // Mapping of resource ID to display name
  metadata: {
    time_range: TimeRange;
    frequency: Frequency;
  };
}

// Exposure specific types
export type ExposureType = string;

export interface ExposureRequest {
  resources: Resource[];
  exposure_type: ExposureType;
  as_at: string;
  group_smaller_items?: boolean;
  top_n_items?: number;
  weight_type?: "actual" | "model";
}

export interface ExposureDataPoint {
  name: string;
  weight: number;
  children?: ExposureDataPoint[];
}

// Define the structure for the data associated with each portfolio key
interface ExposurePortfolioData {
  cols: { id: string; label: string; type: string }[];
  rows: (string | number)[][];
  statistics?: any | null; // Add statistics if it exists
}

export interface ExposureResponse {
  // The API returns a dictionary directly, mapping portfolio keys to their data.
  [portfolioKey: string]: ExposurePortfolioData;
}

export type ExposureDisplayMode = "weights" | "scaled_weights" | "values";

export type ExposureTimeseriesFrequency = "monthly" | "quarterly";

export interface ExposureTimeseriesRequest {
  resources: Resource[];
  exposure_type: ExposureType;
  exposure_variant?: "net" | "long" | "short";
  time_range: TimeRange;
  frequency?: ExposureTimeseriesFrequency;
  display_mode?: ExposureDisplayMode;
}

export interface ExposureTimeseriesMetadata {
  requested_dates: string[];
  available_dates: string[];
  missing_dates: string[];
  categories: string[];
  display_mode: ExposureDisplayMode;
  frequency: ExposureTimeseriesFrequency;
  exposure_type: string;
  exposure_variant: string;
  time_range: {
    start_date: string;
    end_date: string;
    preset: string | null;
  };
  resource: {
    key: string;
    name: string;
    type: string;
    resource_id: string;
  };
  portfolio_value_by_date?: Record<string, number>;
}

export interface ExposureTimeseriesResponse {
  data: {
    cols: { id: string; label: string; type: string }[];
    rows: Array<Array<number | null>>;
  };
  metadata: ExposureTimeseriesMetadata;
}

// Categorical chart types
export interface CategoricalChartRequest {
  as_at?: string;
  resources: Resource[];
  /** Screener field paths to fetch and use as categories */
  fields: string[];
}

export interface CategoricalChartColumnDefinition {
  id: string; // e.g., "category" | "value"
  label: string;
  type: "string" | "number";
}

export interface CategoricalChartSeriesData {
  cols: CategoricalChartColumnDefinition[];
  rows: Array<[string, number | null]>; // [category, value]
  statistics: any | null;
}

export interface CategoricalChartResponse {
  data: {
    // key like "portfolio:port-xxx" or "benchmark:child-bm-xxx"
    [resourceKey: string]: {
      combined: CategoricalChartSeriesData;
    };
  };
  metadata: {
    as_at: string;
    fields: string[];
    resources_processed: number;
    total_resources_requested: number;
    total_fields_requested: number;
  };
}

// ScatterChart specific types
export interface ScatterChartRequest {
  resources: Resource[];
  x_statistic: string;
  y_statistic: string;
  x_label?: string;
  y_label?: string;
  include_labels?: boolean;
  as_at?: string; // Optional as_at date (YYYY-MM-DD format)
}

export interface ScatterChartColumnDefinition {
  id: string;
  label: string;
  type: "string" | "number";
}

export interface ScatterChartResponse {
  cols: ScatterChartColumnDefinition[];
  rows: Array<[string, string, number, number]>; // [resource_id, resource_name, x_value, y_value]
}

// Lookthrough/Exposure Breakdown specific types
export interface LookthroughResource {
  resource_type: "portfolio" | "product";
  resource_id: string;
}

export interface LookthroughRequest {
  resources: LookthroughResource[];
  exposure_type: string;
  include_underlying?: boolean;
  enable_lookthrough?: boolean;
  as_at?: string;
  use_primary_only?: boolean;
  weight_type?: "actual" | "model";
}

export interface LookthroughResponse {
  [portfolioKey: string]: {
    resource_key: string;
    categories: Array<{
      category_name: string;
      category_weight: number;
      sub_categories: Array<{
        sub_category_name: string;
        sub_category_weight: number;
        holdings: Array<{
          name: string;
          weight: number;
        }>;
        holdings_count: number;
      }>;
    }>;
    total_coverage: number;
    unmapped_holdings: Array<{
      name: string;
      weight: number;
    }>;
    unmapped_weight: number;
  };
}

// Playground types
export enum PlaygroundType {
  PerformanceHistory = "performance-history",
  RollingStats = "rolling-stats",
  MetricsOverTime = "metrics-over-time",
  Exposure = "exposure",
  ExposureColumns = "exposure-columns",
  ExposureOverTime = "exposure-over-time",
  ScatterChart = "scatter-chart",
  Lookthrough = "lookthrough",
  StatisticsTable = "statistics-table",
  Underwater = "underwater",
  CorrelationMatrix = "correlation-matrix",
}

export interface PlaygroundConfig {
  type: PlaygroundType;
  theme?: "light" | "dark";
}

// Holdings specific types
export interface HoldingsResource {
  resource_type: "portfolio" | "product";
  resource_id: string;
}

export interface HoldingsRequest {
  resources: HoldingsResource[];
  as_at: string;
  columns?: string[];
  sort_by?: string;
  sort_direction?: "asc" | "desc";
  limit?: number;
  enable_lookthrough?: boolean;
  /** If true, aggregate lookthrough holdings up the hierarchy when available */
  aggregate_lookthrough?: boolean;
  max_lookthrough_depth?: number;
  /** When true, enrich product_id_map with name link URLs (feature-flag gated) */
  include_holding_name_links?: boolean;
}

// Updated types to match the actual API response
export interface HoldingsColumnDefinition {
  id: string;
  label: string;
  type: "string" | "number";
}

export interface HoldingsResourceData {
  cols: HoldingsColumnDefinition[];
  rows: Array<Array<string | number>>;
  statistics: any | null;
}

export interface HoldingsProductMapping {
  product_id: string;
  snapshot_id: string;
  name_link_url?: string | null;
}

export interface HoldingsResponse {
  data: {
    [resourceId: string]: HoldingsResourceData;
  };
  metadata: {
    as_at: string;
    sort_by: string;
    sort_direction: string;
    total_resources: number;
    resources_with_holdings: number;
    columns_included: string[];
    product_id_maps: {
      [resourceId: string]: {
        [rowKey: string]: HoldingsProductMapping;
      };
    };
    limit: number;
    portfolio_names?: string[];
    holding_name_link_column?: string;
  };
}

// Holdings comparison types for multi-portfolio view
export interface HoldingsComparisonRequest {
  resources: { resource_type: string; resource_id: string }[];
  resource_names?: Record<string, string>;
  as_at: string;
  columns?: string[];
  sort_by?: string;
  sort_direction?: "asc" | "desc";
  limit?: number;
  show_diff?: boolean;
  enable_lookthrough?: boolean;
  max_lookthrough_depth?: number;
  aggregate_lookthrough?: boolean;
}

export interface HoldingsComparisonColumn {
  id: string;
  label: string;
  type: "string" | "number";
}

export interface HoldingsComparisonResponse {
  cols: HoldingsComparisonColumn[];
  rows: Array<Array<string | number | null>>;
  metadata: {
    as_at: string;
    total_portfolios: number;
    total_holdings: number;
    portfolio_ids: string[];
    portfolio_names?: string[];
    sort_by?: string;
    sort_direction?: string;
    limit?: number;
  };
}

// Transformed holding item for easier consumption in components
export interface HoldingItem {
  id: string;
  name: string;
  weight: number;
  effective_weight?: number;
  effective_real_weight?: number;
  lookthrough_depth?: number;
  is_portfolio_holding?: boolean;
  is_expanded?: boolean;
  resource_id?: string;
  [key: string]: string | number | boolean | undefined; // For additional columns
}

// Contributions specific types
export interface ContributionsRequest {
  portfolio_id: string;
  time_range: {
    preset?: string;
    start_date?: string;
    end_date?: string;
  };
  include_weights?: boolean;
  include_performance?: boolean;
  sort_by?: string;
  sort_direction?: "asc" | "desc" | "custom";
  custom_sort_order?: string[];
  secondary_sort_by?: string;
  secondary_sort_direction?: "asc" | "desc" | "custom";
  secondary_custom_sort_order?: string[];
  limit?: number;
  enable_lookthrough?: boolean;
  max_lookthrough_depth?: number;
  aggregate_lookthrough?: boolean;
  exposure_columns?: string[];
  snapshot_columns?: string[];
  contribution_group_by?: string;
  show_all_categories?: boolean;
}

export interface ContributionsColumnDefinition {
  id: string;
  label: string;
  type: "string" | "number";
}

export interface ContributionsResourceData {
  cols: ContributionsColumnDefinition[];
  rows: Array<Array<string | number>>;
  statistics: any | null;
}

export interface ContributionsResponse {
  [resourceId: string]: ContributionsResourceData;
}

// Contributions comparison types for multi-portfolio view
export interface ContributionsComparisonRequest {
  resources: { resource_type: string; resource_id: string }[];
  resource_names?: Record<string, string>;
  time_range: TimeRange;
  columns?: string[];
  sort_by?: string;
  sort_direction?: "asc" | "desc" | "custom";
  custom_sort_order?: string[];
  secondary_sort_by?: string;
  secondary_sort_direction?: "asc" | "desc" | "custom";
  secondary_custom_sort_order?: string[];
  limit?: number;
  show_diff?: boolean;
  enable_lookthrough?: boolean;
  max_lookthrough_depth?: number;
  aggregate_lookthrough?: boolean;
  include_weights?: boolean;
  include_performance?: boolean;
  exposure_columns?: string[];
  snapshot_columns?: string[];
  contribution_group_by?: string;
  show_all_categories?: boolean;
}

export interface ContributionsComparisonColumn {
  id: string;
  label: string;
  type: "string" | "number";
}

export interface ContributionsComparisonResponse {
  data: {
    comparison: {
      cols: ContributionsComparisonColumn[];
      rows: Array<Array<string | number | null>>;
      statistics: any | null;
    };
  };
  metadata: {
    start_date: string;
    end_date: string;
    time_range: TimeRange | null;
    sort_by?: string;
    sort_direction?: string;
    total_resources: number;
    columns_included: string[];
    portfolio_ids: string[];
    portfolio_names?: string[];
    is_comparison: boolean;
  };
}

// Exposures comparison types for multi-portfolio view
export interface ExposuresComparisonRequest {
  resources: { resource_type: string; resource_id: string }[];
  resource_names?: Record<string, string>;
  exposure_type: string;
  exposure_variant?: "net" | "long" | "short";
  as_at?: string;
  sort_by?: string;
  sort_direction?: "asc" | "desc" | "custom";
  custom_sort_order?: string[];
  limit?: number;
  show_diff?: boolean;
}

export interface ExposuresComparisonColumn {
  id: string;
  label: string;
  type: "string" | "number";
}

export interface ExposuresComparisonResponse {
  data: {
    comparison: {
      cols: ExposuresComparisonColumn[];
      rows: Array<Array<string | number | null>>;
      statistics: any | null;
    };
  };
  metadata: {
    as_at?: string;
    exposure_type: string;
    exposure_variant: string;
    sort_by?: string;
    sort_direction?: string;
    total_resources: number;
    columns_included: string[];
    portfolio_ids: string[];
    portfolio_names?: string[];
    is_comparison: boolean;
  };
}

// Transformed contribution item for easier consumption in components
export interface ContributionItem {
  id: string;
  name: string;
  contribution: number;
  weight?: number;
  avg_weight?: number;
  start_weight?: number;
  end_weight?: number;
  start_value_portfolio_ccy?: number;
  end_value_portfolio_ccy?: number;
  effective_contribution?: number;
  lookthrough_depth?: number;
  is_portfolio_contribution?: boolean;
  is_expanded?: boolean;
  [key: string]: string | number | boolean | undefined; // For additional columns
}

// Widget Configuration types
export interface WidgetConfig {
  config_id: string;
  type: string;
  name: string;
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CreateWidgetConfigRequest {
  type: string;
  name: string;
  config: Record<string, any>;
}

// TWR (Time Weighted Return) types
export interface TWRRequest {
  portfolio_id: string;
  time_range: {
    start_date?: string;
    end_date?: string;
    preset?: string;
  };
  sort_by?: string;
  sort_direction?: "asc" | "desc";
  group_by?: string;
  exclusions?: string[];
}

export interface TWRResponse {
  table: {
    cols: Array<{
      id: string;
      label: string;
      type: string;
    }>;
    rows: Array<Array<any>>;
  };
  metadata: {
    portfolio_id: string;
    time_range: {
      start_date: string;
      end_date: string;
      preset?: string;
    };
    sort_by?: string;
    sort_direction?: string;
    group_by?: string;
    product_id_map: Record<string, any>;
  };
}

// IRR (Internal Rate of Return) types
export interface IRRRequest {
  portfolio_id: string;
  start_date: string;
  end_date: string;
  sort_by?: string;
  sort_direction?: "asc" | "desc";
  group_by?: string;
  exclusions?: string[];
}

export interface IRRResponse {
  table: {
    cols: Array<{
      id: string;
      label: string;
      type: string;
    }>;
    rows: Array<Array<any>>;
  };
  metadata: {
    portfolio_id: string;
    start_date: string;
    end_date: string;
    total_holdings: number;
    sort_by?: string;
    sort_direction?: string;
    group_by?: string;
    holding_id_map: Record<string, any>;
  };
}
